// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/useStartNewProjectChat.test.ts
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { campaignRoute } from "./campaignRoute";

import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import { askPunttKeys } from "../../utils/queries/askPuntt";
import {
  askPunttProjectKeys,
  useCreateAskPunttProject,
} from "../../utils/queries/askPunttProjects";

/** Default name until the user renames the project in the workspace. */
const DEFAULT_NEW_PROJECT_NAME = "New project";

/**
 * Creates a first-class Ask Puntt project (shared chat is seeded server-side
 * with the starter + upload prompt) and opens the project workspace — not the
 * standalone `/ask-puntt/$chatId` route.
 */
export function useStartNewProjectChat(analyticsSurface: string) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createProject = useCreateAskPunttProject();
  const analytics = useAnalytics(analyticsSurface);

  const startNewProjectChat = async (source: string) => {
    try {
      const result = await createProject.mutateAsync({
        name: DEFAULT_NEW_PROJECT_NAME,
      });
      const projectId = result.project._id;
      const { sharedChatId, sharedChatBootstrap } = result;

      if (sharedChatBootstrap && sharedChatId) {
        queryClient.setQueryData(
          askPunttKeys.messages(sharedChatId),
          sharedChatBootstrap,
        );
      }

      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.feed(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.timeline(projectId),
      });

      analytics.capture("ask_puntt_project_started_from_launcher", {
        source,
        projectId,
        sharedChatId,
      });
      navigate({
        to: campaignRoute.to,
        params: { projectId },
      });
    } catch (error) {
      analytics.capture("ask_puntt_project_create_error", {
        source,
        ...errorAnalyticsPayload(error),
      });
      console.error("Failed to start new project:", error);
    }
  };

  return {
    startNewProjectChat,
    isPending: createProject.isPending,
  };
}
